import React, { useState } from 'react';
import {
  EmailSMSConfigWrapper,
  EmailSMSConfigSidebar,
  EmailSMSConfigSidebarItem,
  EmailSMSConfigSidebarItemActive,
  EmailSMSConfigSidebarCollapse,
  EmailSMSConfigSidebarCollapseHeader,
  EmailSMSConfigSidebarCollapseIcon,
  EmailSMSConfigSidebarSubItem
} from './EmailSmsStyled';
import GeneralConfig from './GeneralConfig';
import ESignEmailConfig from './ESignEmailConfig';
import Loader from "../../../components/Loader/Loader";
import ErrorToast from "../../../components/Notification/ErrorToast";
import { Tooltip } from "antd";

const sidebarStructure = [
  { type: 'item', label: 'General Configurations' },
  {
    type: 'collapse',
    label: 'Email Content Configurations',
    icon: require('../../../assets/images/Collapse.png'),
    children: [
      { label: 'E-Signature Request Email', type: 'SIGNATURE_REQUEST' },
      { label: 'E-Signature Reminder Email', type: 'SIGNATURE_REMINDER' },
      { label: 'External Share Email', type: 'EXTERNAL_SHARE' },
    ],
  },
  {
    type: 'collapse',
    label: 'SMS Content Configuration',
    icon: require('../../../assets/images/Collapse.png'),
    children: [
      { label: 'E-Signature SMS' },
      { label: 'E-Signature Reminder SMS' },
    ],
  },
];

const flattenSidebar = (structure) => {
  const flat = [];
  for (const item of structure) {
    if (item.type === 'item') {
      flat.push({ type: 'item', label: item.label });
    } else if (item.type === 'collapse') {
      flat.push({ type: 'collapse', label: item.label });
      for (const child of item.children) {
        flat.push({ type: 'subitem', label: child.label, parent: item.label, API_TYPE: child.type });
      }
    }
  }
  return flat;
};

const flatSidebarList = flattenSidebar(sidebarStructure);

const EmailSMSConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [openCollapses, setOpenCollapses] = useState({
    'Email Content Configurations': true,
    'SMS Content Configuration': true,
  });

  const handleToaster = (message, success = false) => {
    setIsLoading(false);
    const id = Date.now().toString();
    setError([{ id, newMsg: message, success }]);
    setShowNotification(true);
  };

  const renderItem = (item, idx) => {
    const isActive = activeIndex === idx;
    const Component = isActive ? EmailSMSConfigSidebarItemActive : EmailSMSConfigSidebarItem;
    return (
      <Component key={item.label} onClick={() => setActiveIndex(idx)}>
        <Tooltip
          key={item.label}
          placement="right"
          title={item.label}
          getPopupContainer={() => document.body}
          styles={{ body: { zIndex: 2000 }}}
        >
          {item.label}
        </Tooltip>
      </Component>
    );
  };

  const renderCollapse = (item, idx) => {
    const isOpen = openCollapses[item.label];
    const icon = sidebarStructure.find(s => s.label === item.label)?.icon;

    return (
      <EmailSMSConfigSidebarCollapse key={item.label}>
        <EmailSMSConfigSidebarCollapseHeader
          onClick={() =>
            setOpenCollapses(prev => ({
              ...prev,
              [item.label]: !prev[item.label],
            }))
          }
        >
          <span>{item.label}</span>
          <EmailSMSConfigSidebarCollapseIcon
            src={icon}
            alt="Collapse"
            open={isOpen}
          />
        </EmailSMSConfigSidebarCollapseHeader>

        {isOpen && renderSubItems(item.label)}
      </EmailSMSConfigSidebarCollapse>
    );
  };

  const renderSubItems = (parentLabel) =>
    flatSidebarList.map((sub, idx) => {
      if (sub.type === 'subitem' && sub.parent === parentLabel) {
        return (
          <Tooltip
            key={sub.label}
            placement="right"
            title={sub.label}
            getPopupContainer={() => document.body}
            styles={{ body: { zIndex: 2000 }}}
          >
            <EmailSMSConfigSidebarSubItem
              className={activeIndex === idx ? 'active' : ''}
              onClick={() => setActiveIndex(idx)}
            >
              <span>{sub.label}</span>
            </EmailSMSConfigSidebarSubItem>
          </Tooltip>
        );
      }
      return null;
    });

  const renderSidebar = () => (
    <EmailSMSConfigSidebar>
      {flatSidebarList.map((item, idx) => {
        if (item.type === 'item') return renderItem(item, idx);
        if (item.type === 'collapse') return renderCollapse(item, idx);
        return null; // subitems are rendered inside collapse
      })}
    </EmailSMSConfigSidebar>
  );

  const activeLabel = flatSidebarList[activeIndex]?.label;
  const activeType = flatSidebarList[activeIndex];

  return (
    <EmailSMSConfigWrapper>
      {isLoading && <Loader />}
      {renderSidebar()}
      {activeLabel === 'General Configurations' && (
        <GeneralConfig setIsLoading={setIsLoading} handleToaster={handleToaster} />
      )}
      {(activeLabel === 'E-Signature Request Email' || activeLabel === 'E-Signature Reminder Email' || activeLabel === 'External Share Email') && (
        <ESignEmailConfig activeLabel={activeLabel} activeType={activeType} setIsLoading={setIsLoading} handleToaster={handleToaster} isLoading={isLoading} />
      )}
      {(activeLabel === 'E-Signature SMS' || activeLabel === 'E-Signature Reminder SMS') && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 32px', color: '#595959' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>&#9993;</div>
          <h3 style={{ margin: '0 0 8px', color: '#262626', fontSize: 16, fontWeight: 600 }}>
            {activeLabel === 'E-Signature SMS' ? 'E-Signature SMS' : 'E-Signature Reminder SMS'}
          </h3>
          <p style={{ margin: 0, textAlign: 'center', maxWidth: 480, lineHeight: 1.6 }}>
            SMS message content is managed by Formstack Sign. To customize the text,
            log in to your{' '}
            <a href="https://my.insuresign.com" target="_blank" rel="noopener noreferrer">
              Formstack Sign dashboard
            </a>{' '}
            and update your account's SMS templates there.
          </p>
        </div>
      )}
      {showNotification && (
        <ErrorToast
          message={error}
          onClose={(id) =>
            setError((prev) => prev.filter((msg) => msg.id !== id))
          }
        />
      )}
    </EmailSMSConfigWrapper>
  );
};

export default EmailSMSConfig;
